import { withRetry, UPDATE_CASE_ACTION } from '@demandproai/shared';
import { postRequest } from './axiosClient';
import Constants from '../Constants';
import { resolveFileMimeType } from './imagePreview';
import { buildDiscoveryExhibitPaths } from './caseFieldPaths';
import store from '../redux/store';

export const updateCaseField = async ({ caseId, fieldKey, value, action = 'update' }) => {
    const updateWithRetry = async () => {
        const response = await postRequest({
            url: `/updateCase/${caseId}`,
            data: { action, data: { [fieldKey]: value } },
        });

        if (response?.status === 200) {
            return response.data;
        }

        throw new Error(`Failed to update case field. Status: ${response?.status}`);
    };

    return withRetry(updateWithRetry);
};

export const updateDiscoverySetFileField = async ({ discoverySetId, updateField, file }) => {
    const updateWithRetry = async () => {
        const response = await postRequest({
            url: Constants.ApiUrl.discoverySet.uploadFile,
            data: { discoverySetId, updateField, file },
        });

        if (response?.status === 200 || response?.status === 201) {
            return response.data;
        }

        throw new Error(`Failed to persist discovery set field. Status: ${response?.status}`);
    };

    return withRetry(updateWithRetry);
};

export const getPresignedUrl = async ({ file, fileName, caseId, metaData = {}, purpose }) => {
    const stringifiedMetaData = {};
    for (const key in metaData) {
        stringifiedMetaData[key] = typeof metaData[key] === 'string'
            ? metaData[key]
            : JSON.stringify(metaData[key]);
    }
    stringifiedMetaData.originalFileName = fileName;

    const response = await postRequest({
        url: Constants.ApiUrl.preSignedUrls,
        data: {
            fileName,
            fileType: resolveFileMimeType(file),
            caseId,
            metaData: stringifiedMetaData,
            purpose
        },
    });

    if (response?.status === 200) return response.data;

    throw new Error('Failed to get presigned URL');
};

export const buildFilesWithMetadata = (files, presignedData) =>
    files.map((fileData, i) => ({
        name: fileData.name || fileData.file.name,
        s3UrlPath: presignedData[i].uniqueKey,
        file: fileData.file,
        uniqueKey: presignedData[i].uniqueKey,
        metaData: fileData.metaData,
    }));

export const buildUploadTasks = ({ files, presignedData, caseId, caseRecordType, callback, hasInlineProgress = false }) =>
    files.map((fileData, i) => ({
        id: `${Date.now()}-${i}-${Math.random().toString(36).substring(2, 11)}`,
        file: fileData.file,
        fileName: fileData.name || fileData.file.name,
        caseId,
        caseRecordType,
        metaData: fileData.metaData || {},
        presignedUrl: presignedData[i].signedUrl,
        uniqueKey: presignedData[i].uniqueKey,
        callback,
        // callers with their own progress list opt out of the global UploadProgressWidget
        hasInlineProgress,
    }));

export const buildDiscoveryDedicateCallback = ({ discoverySetId, updateField }) =>
    async ({ fileName, uniqueKey, metaData }) => {
        if (!discoverySetId) return;
        await updateDiscoverySetFileField({
            discoverySetId,
            updateField,
            file: { name: fileName, s3UrlPath: uniqueKey, uniqueKey, metaData },
        });
    };

export const buildCaseFieldUpdateCallback = ({ fieldKey, action = 'update' }) =>
    async ({ caseId, fileName, uniqueKey }) => {
        if (!fieldKey) return;
        const fileData = store.getState().fileUpload.caseDataFiles.get(uniqueKey);
        if (!fileData) return;
        await updateCaseField({
            caseId,
            fieldKey,
            value: {
                name: fileName,
                s3UrlPath: uniqueKey,
                ...(fileData.isSelected && { isSelected: fileData.isSelected }),
                ...(fileData.isLiability && { isLiability: fileData.isLiability }),
                ...(fileData.metaData?.defendants && { defendants: fileData.metaData.defendants }),
            },
            action,
        });
    };

// pushes the finished upload onto the exhibit so the document persists without an explicit save
export const buildDiscoveryExhibitDocumentCallback = ({ exhibitIndex }) =>
    async ({ caseId, fileName, uniqueKey }) => {
        await updateCaseField({
            caseId,
            fieldKey: buildDiscoveryExhibitPaths().documents(exhibitIndex),
            value: { title: fileName, s3UrlPath: uniqueKey },
            action: UPDATE_CASE_ACTION.ADD,
        });
    };

export const buildHeadshotFile = (files, presignedData) =>
    files.map((fileData, i) => ({
        id: `${Date.now()}-${i}-${Math.random().toString(36).substring(2, 11)}`,
        file: fileData.file,
        fileName: fileData.name || fileData.file.name,
        presignedUrl: presignedData[i].signedUrl,
        uniqueKey: presignedData[i].uniqueKey,
    }));
